import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListItem} from '@github-ui/list-view'
import {
  forksListPath,
  orgTopicReposPath,
  repoCommitActivityInsightsPath,
  repoIssuesPath,
  repoPullRequestsPath,
  repositoryParticipationPath,
  repositoryPath,
  repoStargazersPath,
} from '@github-ui/paths'
import type {PrimaryLanguage, RepositoryItem} from '@github-ui/repos-list-shared'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {ObservableBox} from '@github-ui/use-sticky-header/ObservableBox'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {
  GitPullRequestIcon,
  type Icon,
  IssueOpenedIcon,
  LawIcon,
  RepoCloneIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  StarIcon,
} from '@primer/octicons-react'
import type {BaseStylesProps, SxProp} from '@primer/react'
import {Box, Label, Link, Octicon, RelativeTime, Text, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/drafts'
import {useRef, useState} from 'react'

import {LanguageCircle} from './LanguageCircle'
import {Sparkline} from './Sparkline'

interface Props extends SxProp {
  repo: RepositoryItem
}

interface FlexProps extends SxProp {
  flex?: number
}

interface BaseListItemProps extends Props {
  dropExtras?: boolean
  headerSx?: FlexProps
}

const smallScreenOnly: BaseStylesProps = {display: ['block', 'none']}
const hideOnSmallScreen: BaseStylesProps = {display: ['none', 'block']}

const MAX_TOPICS_WIDE_SCREEN = 10
const MAX_TOPICS_NARROW_SCREEN = 3

const sparklineWidth = 120
const tinySparklineWidth = 60

export function BaseListItem({repo, sx, dropExtras = false, headerSx = {}}: BaseListItemProps) {
  const {name, type, owner, description, lastUpdated, license, allTopics} = repo

  const dropExtrasSx = dropExtras ? {display: 'none'} : {}

  return (
    <ListItem
      sx={sx}
      title={
        <ListItem.Title
          value={name}
          href={repositoryPath({owner, repo: name})}
          containerSx={{display: 'inline-flex', alignItems: 'center', maxWidth: '100%'}}
          headerSx={{
            pl: 2,
            fontSize: 2,
            fontWeight: 'semibold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            ...headerSx,
          }}
        >
          <Label sx={{mx: 2}} variant="secondary">
            {type}
          </Label>
          <ParticipationSparkline repo={repo} sx={dropExtrasSx} />
        </ListItem.Title>
      }
    >
      <ListItem.MainContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            color: 'fg.muted',
            gap: 2,
            width: ['100%', '100%', `calc(100% - ${sparklineWidth}px)`],
            pl: 2,
          }}
        >
          {description && (
            <ListItem.Description>
              <FormattedDescription sx={{fontSize: 1}} description={description} />
            </ListItem.Description>
          )}

          {allTopics.length > 0 && <TopicsLabels {...{...repo, dropExtras}} />}

          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 0}}>
            <LanguageLabel {...repo} />
            {repo.primaryLanguage && <Text>•</Text>}

            {license && !dropExtras && (
              <>
                <LicenseLabel {...repo} />
                <Text>•</Text>
              </>
            )}

            <ForksLabel {...repo} />
            <Text>•</Text>

            <StarsLabel {...repo} />
            <Text>•</Text>

            {repo.issueCount !== null && !dropExtras && (
              <>
                <IssuesLabel issueCount={repo.issueCount} owner={repo.owner} name={repo.name} />
                <Text>•</Text>
              </>
            )}

            {repo.pullRequestCount !== null && (
              <>
                <PullRequestsLabel pullRequestCount={repo.pullRequestCount} owner={repo.owner} name={repo.name} />
                <Text sx={dropExtrasSx}>•</Text>
              </>
            )}

            <Text sx={{overflow: 'hidden', whiteSpace: 'nowrap', ...hideOnSmallScreen}}>
              {lastUpdated.hasBeenPushedTo ? 'Updated' : 'Created'} <RelativeTime datetime={lastUpdated.timestamp} />
            </Text>

            <RelativeTime
              datetime={lastUpdated.timestamp}
              format="micro"
              sx={dropExtras ? {display: 'none'} : {...smallScreenOnly}}
            />
          </Box>
        </Box>
      </ListItem.MainContent>
    </ListItem>
  )
}

export function DefaultListItem({repo, sx}: Props) {
  return <BaseListItem repo={repo} sx={sx} />
}

export function NarrowCompactListItem({repo, sx}: Props) {
  return <BaseListItem repo={repo} sx={sx} dropExtras headerSx={{flex: 1}} />
}

export function CompactListItem({repo, sx}: Props) {
  const {name, owner, description} = repo
  return (
    <Box
      as="li"
      sx={{
        ...sx,
        flexDirection: 'row',
        alignItems: 'baseline',
        p: 2,
        pl: 3,
        gap: 2,
        borderBottom: '1px solid',
        borderColor: 'border.subtle',
      }}
    >
      <Octicon icon={getRepoIcon(repo)} sx={{alignSelf: 'center', color: 'fg.muted'}} />
      <Truncate inline title={name} sx={{flex: 1, minWidth: 0, maxWidth: 'fit-content'}}>
        <Link
          sx={{fontSize: 2, fontWeight: 'semibold', color: 'fg.default'}}
          href={repositoryPath({owner, repo: name})}
        >
          {name}
        </Link>
      </Truncate>

      <Truncate inline title={description} sx={{flex: 1, minWidth: 0, maxWidth: '100%'}}>
        <FormattedDescription description={description} />
      </Truncate>

      <Box key="language" sx={{display: 'flex', width: '90px'}}>
        <LanguageLabel truncate {...repo} />
      </Box>
      <Box key="forks" sx={{alignSelf: 'center', width: '52px'}}>
        <ForksLabel {...repo} />
      </Box>
      <Box key="stars" sx={{alignSelf: 'center', width: '52px'}}>
        <StarsLabel {...repo} />
      </Box>
    </Box>
  )
}

function getRepoIcon(repo: RepositoryItem) {
  const {isFork, type} = repo
  const lowerCaseType = type.toLowerCase()

  let icon
  if (isFork) {
    icon = RepoForkedIcon
  } else if (lowerCaseType.includes('mirror')) {
    icon = RepoCloneIcon
  } else if (lowerCaseType.includes('private') || lowerCaseType.includes('internal')) {
    icon = RepoLockedIcon
  } else {
    icon = RepoIcon
  }

  return icon
}

function pluralizeTopic(count: number) {
  return count === 1 ? 'topic' : 'topics'
}

function TopicLabel({topic, owner, sx = {}}: {topic: string; owner: string; sx?: BaseStylesProps}) {
  return (
    <Link key={topic} href={orgTopicReposPath({topic, org: owner})} data-testid={`${topic}-topic-link`} sx={sx}>
      <Label
        size="large"
        sx={{
          border: '0',
          color: 'accent.fg',
          backgroundColor: 'accent.subtle',
          fontWeight: 500,
          '&:hover': {
            color: 'fg.onEmphasis',
            backgroundColor: 'accent.fg',
          },
        }}
      >
        {topic}
      </Label>
    </Link>
  )
}

function MoreTopicsButton({
  notShown,
  sx,
  setIsOpen,
}: {
  notShown: number
  sx: BaseStylesProps
  setIsOpen: (value: boolean) => void
}) {
  return notShown > 0 ? (
    <Label
      as="button"
      aria-label={`Show ${notShown} more ${pluralizeTopic(notShown)}`}
      sx={{fontWeight: 500, ...sx}}
      size="large"
      variant="secondary"
      onClick={() => setIsOpen(true)}
    >
      + {notShown}
    </Label>
  ) : null
}

function TopicsLabels({
  allTopics,
  owner,
  name,
  dropExtras,
}: {
  allTopics: string[]
  owner: string
  name: string
  dropExtras: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)

  const narrowTopicNames = allTopics.slice(0, MAX_TOPICS_NARROW_SCREEN)
  const narrowTopicsNotShown = allTopics.length - MAX_TOPICS_NARROW_SCREEN

  const wideOnlyTopicNames = allTopics.slice(MAX_TOPICS_NARROW_SCREEN, MAX_TOPICS_WIDE_SCREEN)
  const topicsNotShown = Math.max(0, allTopics.length - MAX_TOPICS_WIDE_SCREEN)

  return (
    <Box
      sx={{
        display: dropExtras ? 'none' : 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {narrowTopicNames.map(topic => (
        <TopicLabel key={topic} topic={topic} owner={owner} />
      ))}
      <MoreTopicsButton notShown={narrowTopicsNotShown} sx={smallScreenOnly} setIsOpen={setIsOpen} />

      {wideOnlyTopicNames.map(topic => (
        <TopicLabel key={topic} topic={topic} owner={owner} sx={hideOnSmallScreen} />
      ))}
      <MoreTopicsButton notShown={topicsNotShown} sx={hideOnSmallScreen} setIsOpen={setIsOpen} />

      {isOpen && (
        <Dialog
          title={`Topics for ${owner}/${name}`}
          width="small"
          position={{narrow: 'bottom', wide: 'center'}}
          footerButtons={[{content: 'Done', onClick: () => setIsOpen(false)}]}
          onClose={() => setIsOpen(false)}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {allTopics.map(topic => (
              <Link
                key={topic}
                href={orgTopicReposPath({topic, org: owner})}
                data-testid={`dialog-${topic}-topic-link`}
              >
                <Label
                  size="large"
                  sx={{
                    border: '0',
                    color: 'accent.fg',
                    backgroundColor: 'accent.subtle',
                    fontWeight: 500,
                    '&:hover': {
                      color: 'fg.onEmphasis',
                      backgroundColor: 'accent.fg',
                    },
                  }}
                >
                  {topic}
                </Label>
              </Link>
            ))}
          </Box>
        </Dialog>
      )}
    </Box>
  )
}

function LanguageLabel({primaryLanguage, truncate}: {primaryLanguage: PrimaryLanguage | null; truncate?: boolean}) {
  if (!primaryLanguage) {
    return null
  }

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      <LanguageCircle color={primaryLanguage.color} />
      {truncate ? (
        <Truncate inline title={primaryLanguage.name} sx={{maxWidth: 70}}>
          {primaryLanguage.name}
        </Truncate>
      ) : (
        <Text sx={{whiteSpace: 'nowrap'}}>{primaryLanguage.name}</Text>
      )}
    </Box>
  )
}

function LicenseLabel({license}: {license: string | null}) {
  if (!license) {
    return null
  }

  return <IconLabel content={license} icon={LawIcon} />
}

function StarsLabel({starsCount, owner, name}: {starsCount: number; owner: string; name: string}) {
  const count = human(starsCount, {precision: getSignificanceBasedPrecision(starsCount)})
  const unit = count === '1' ? 'star' : 'stars'
  const label = `${count} ${unit}`
  return <IconLabel aria-label={label} content={count} icon={StarIcon} href={repoStargazersPath(owner, name)} />
}

function ForksLabel({forksCount, owner, name}: {forksCount: number; owner: string; name: string}) {
  const count = human(forksCount, {precision: getSignificanceBasedPrecision(forksCount)})
  const unit = count === '1' ? 'fork' : 'forks'
  const label = `${count} ${unit}`
  return (
    <IconLabel aria-label={label} content={count} icon={RepoForkedIcon} href={forksListPath({owner, repo: name})} />
  )
}

function IssuesLabel({issueCount, owner, name}: {issueCount: number; owner: string; name: string}) {
  const count = human(issueCount, {precision: getSignificanceBasedPrecision(issueCount)})
  const unit = count === '1' ? 'issue' : 'issues'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'issue-count'}
      content={count}
      icon={IssueOpenedIcon}
      href={repoIssuesPath(owner, name)}
    />
  )
}

function PullRequestsLabel({pullRequestCount, owner, name}: {pullRequestCount: number; owner: string; name: string}) {
  const count = human(pullRequestCount, {precision: getSignificanceBasedPrecision(pullRequestCount)})
  const unit = count === '1' ? 'pull request' : 'pull requests'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'pull-request-count'}
      content={count}
      icon={GitPullRequestIcon}
      href={repoPullRequestsPath(owner, name)}
    />
  )
}

const labelContainerSx = {display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap'}
function IconLabel({
  icon,
  href,
  content,
  ...props
}: {
  icon: Icon
  href?: string
  content: string
  ['aria-label']?: string
}) {
  if (href) {
    return (
      <Link {...props} muted={true} sx={labelContainerSx} href={href}>
        <Octicon icon={icon} />
        {content}
      </Link>
    )
  }

  return (
    <Box {...props} sx={labelContainerSx}>
      <Octicon icon={icon} />
      {content}
    </Box>
  )
}

function ParticipationSparkline({repo, sx}: Props) {
  const [participation, setParticipation] = useState(repo.participation)
  const isFetchingRef = useRef(false)

  async function fetchParticipation() {
    if (participation || isFetchingRef.current) return
    isFetchingRef.current = true

    const response = await verifiedFetchJSON(repositoryParticipationPath({owner: repo.owner, repo: repo.name}))
    if (response.ok) {
      const bodyText = await response.text()
      const json = bodyText.length > 0 ? JSON.parse(bodyText) : []
      setParticipation(json)
    }
  }

  const [observeRow, unobserveRow] = useIntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) {
      fetchParticipation()
    }
  })

  return participation ? (
    participation.length === 0 ? (
      <Box data-testid="empty-repo-sparkline" />
    ) : (
      <Box sx={{ml: 'auto', pr: 2, ...sx}}>
        <Link
          href={repoCommitActivityInsightsPath({owner: repo.owner, repo: repo.name})}
          aria-label={`${repo.owner}/${repo.name} past year of commit activity`}
          target="_blank"
        >
          <Box sx={hideOnSmallScreen}>
            <Sparkline uniqueKey={repo.name} points={participation} width={sparklineWidth} />
          </Box>
          <Box sx={smallScreenOnly}>
            <Sparkline uniqueKey={`${repo.name}#tiny`} points={participation} width={tinySparklineWidth} />
          </Box>
        </Link>
      </Box>
    )
  ) : (
    <ObservableBox role="presentation" onObserve={observeRow} onUnobserve={unobserveRow} />
  )
}

function FormattedDescription({description, sx}: {description: SafeHTMLString; sx?: BaseStylesProps}) {
  // `repos-list-description` class is required to support `Link underlines` user setting
  return <SafeHTMLText sx={sx} className="repos-list-description" html={description} />
}

try{ BaseListItem.displayName ||= 'BaseListItem' } catch {}
try{ DefaultListItem.displayName ||= 'DefaultListItem' } catch {}
try{ NarrowCompactListItem.displayName ||= 'NarrowCompactListItem' } catch {}
try{ CompactListItem.displayName ||= 'CompactListItem' } catch {}
try{ TopicLabel.displayName ||= 'TopicLabel' } catch {}
try{ MoreTopicsButton.displayName ||= 'MoreTopicsButton' } catch {}
try{ TopicsLabels.displayName ||= 'TopicsLabels' } catch {}
try{ LanguageLabel.displayName ||= 'LanguageLabel' } catch {}
try{ LicenseLabel.displayName ||= 'LicenseLabel' } catch {}
try{ StarsLabel.displayName ||= 'StarsLabel' } catch {}
try{ ForksLabel.displayName ||= 'ForksLabel' } catch {}
try{ IssuesLabel.displayName ||= 'IssuesLabel' } catch {}
try{ PullRequestsLabel.displayName ||= 'PullRequestsLabel' } catch {}
try{ IconLabel.displayName ||= 'IconLabel' } catch {}
try{ ParticipationSparkline.displayName ||= 'ParticipationSparkline' } catch {}
try{ FormattedDescription.displayName ||= 'FormattedDescription' } catch {}